import type {
  IValidationResult,
  RdsInputCustomEvent,
} from '@rabobank/rds-components';
import type { VALIDITY_STATE } from '@rabobank/rds-components/dist/types';
import {
  Component,
  Event,
  EventEmitter,
  Listen,
  Prop,
  State,
  h,
} from '@stencil/core';
import { Constants, Language } from '../../enums/const-enums';
import { getIBAN, isValidBBAN } from '../../utils/iban';
import { focusSignElement, removeSpaces } from '../../utils/utils';
import {
  RassAccountNrTranslations,
  rassAccountNrTranslations,
} from './i18n/rass-account-nr.i18n';

@Component({
  tag: 'rass-account-nr',
  styleUrl: 'rass-account-nr.scss',
})
export class RassAccountNr {
  @State() ibanPrefix: string = Constants.DEFAULT_IBAN_PREFIX;
  @State() bban = '';
  @State() enterKeyClicked = false;
  @State() isBlurred = true;
  @Prop() isDisabled = false;
  @Prop() language = Language.NL;
  @Event({ eventName: 'account:change' })
  accountNumberChangeEvent: EventEmitter;
  ibanPrefixInput!: HTMLDivElement;
  bbanInput!: HTMLRdsInputElement;

  @Listen('account:update', {
    target: 'document',
  })
  handleAccountUpdateEvent(ev: CustomEvent<{ accountNumber: string }>): void {
    this.resolveIBAN(ev.detail.accountNumber);
  }

  handleFocus(): void {
    this.isBlurred = false;
    this.bban = removeSpaces(this.bban);
  }

  handleInput(event: RdsInputCustomEvent<unknown>): void {
    this.resolveIBAN(event.target.value);
    this.emitAccountNumberChangeEvent();
  }

  handleBlur(): void {
    this.isBlurred = true;
    this.resolveIBAN(this.bban);
    this.validateInput();
  }

  handleKeyDown(event: RdsInputCustomEvent<KeyboardEvent>): void {
    this.enterKeyClicked = event.detail.key === 'Enter';
    this.validateInput();
  }

  handleRdsReady(): void {
    this.addValidator();
    this.addIbanPrefixClickListener();
  }

  private emitAccountNumberChangeEvent(): void {
    this.accountNumberChangeEvent.emit({
      valid: this.isValidBBAN(this.bban),
      accountNumber: this.bban,
    });
  }

  private resolveIBAN(bban: string): void {
    if (this.isValidBBAN(bban)) {
      const iban = getIBAN(bban);
      this.ibanPrefix = iban.substring(0, 11);
      this.bban = iban.substring(11);
    } else {
      this.ibanPrefix = Constants.DEFAULT_IBAN_PREFIX as string;
      this.bban = bban;
    }
  }

  isValidBBAN(inputBBAN: string): boolean {
    const bban = removeSpaces(inputBBAN);
    return isValidBBAN(bban);
  }

  addValidator(): void {
    this.bbanInput
      .addValidator((): IValidationResult => {
        const hasError =
          !this.isValidBBAN(this.bban) &&
          ((this.isBlurred && this.bban.length !== 0) ||
            this.bban.length === 9 ||
            this.enterKeyClicked);
        return hasError
          ? {
              message: this.translations.account_number.format_error,
              type: 'customError' as VALIDITY_STATE.CUSTOM_ERROR,
            }
          : {};
      })
      .catch(() => console.warn('Unable to validate the IBAN.'));
  }

  addIbanPrefixClickListener(): void {
    this.ibanPrefixInput.addEventListener('click', () => {
      focusSignElement('rass-data-reknr input');
    });
  }

  get translations(): RassAccountNrTranslations {
    return (
      rassAccountNrTranslations[this.language] ||
      rassAccountNrTranslations[Language.EN]
    );
  }

  validateInput(): void {
    this.bbanInput
      .validate()
      .catch(() => console.warn('Unable to validate the IBAN.'));
  }

  render(): void {
    return (
      <rds-input
        label={this.translations.account_number.label}
        name="account-nr"
        id="rass-data-reknr"
        inputmode="numeric"
        disabled={this.isDisabled}
        value={this.bban}
        ref={(el: HTMLRdsInputElement): HTMLRdsInputElement =>
          (this.bbanInput = el)
        }
        maxLength={9}
        mask={(value: string) => /^\d+$/.test(value) || value === ''}
        onRdsBlur={(): void => this.handleBlur()}
        onRdsInput={(event): void => this.handleInput(event)}
        onRdsFocus={(): void => this.handleFocus()}
        onRdsKeyDown={(event): void => this.handleKeyDown(event)}
        onRdsReady={(): void => this.handleRdsReady()}
      >
        <div
          id="rass-data-prefix"
          slot="input-start"
          ref={(el: HTMLDivElement): HTMLDivElement =>
            (this.ibanPrefixInput = el)
          }
        >
          {this.ibanPrefix}
        </div>
      </rds-input>
    ) as void;
  }
}
